import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "common/components";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArticleIcon from "@mui/icons-material/Article";
import StorageIcon from "@mui/icons-material/Storage";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import HubHeader from "common/components/custom/hub-header/HubHeader";
import { DataSchema, DataFilter, DataMapping } from "../../data/models";
import { MainRoutes } from "common/config/route.config";
import { snowflakeSchemas } from "../../data";
import "./DataMappingInterface.scss";

// Updated to use sourceField and targetField from DataMapping interface

const documentFields = [
  { name: "requirement_id", label: "Requirement ID", type: "string" },
  { name: "category", label: "Category", type: "string" },
  { name: "requirement_text", label: "Requirement", type: "string" },
  { name: "threshold_value", label: "Threshold", type: "number" },
  { name: "priority", label: "Priority", type: "string" },
  { name: "source_document", label: "Source", type: "string" }
];

const DataMappingInterface: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get parameters from URL
  const tableName = searchParams.get("table") || "default_table";
  const selectedColumnsParam = searchParams.get("columns") || "";
  const filtersParam = searchParams.get("filters") || "";

  const selectedColumns = selectedColumnsParam
    ? selectedColumnsParam.split(",")
    : [];

  // Parse filters from URL (simplified for demo)
  const filters: DataFilter[] = filtersParam
    ? JSON.parse(decodeURIComponent(filtersParam))
    : [];

  // Find the schema for the table
  const iotSchema = snowflakeSchemas.find((s) => s.tableName === tableName) || {
    tableName: tableName,
    columns: [],
    recordCount: 0
  };

  const backRoute = `${MainRoutes.DATA_FILTER_BUILDER}?table=${tableName}&columns=${selectedColumnsParam}`;
  const [mappings, setMappings] = useState<DataMapping[]>([]);

  const handleAddMapping = (sourceField: string, targetField: string) => {
    const existing = mappings.find((m) => m.sourceField === sourceField);
    if (existing) {
      setMappings(
        mappings.map((m) =>
          m.sourceField === sourceField ? { ...m, targetField: targetField } : m
        )
      );
    } else {
      setMappings([
        ...mappings,
        {
          id: Date.now().toString(),
          sourceField: sourceField,
          targetField: targetField,
          transformation: "direct"
        }
      ]);
    }
  };

  const handleComplete = () => {
    // Navigate to predictive maintenance dashboard
    navigate(MainRoutes.PREDICTIVE_MAINTENANCE_DASHBOARD);
  };

  const handleBack = () => {
    // Navigate back to filter builder with parameters
    navigate(
      `${MainRoutes.DATA_FILTER_BUILDER}?table=${tableName}&columns=${selectedColumnsParam}`
    );
  };

  const getMappedTarget = (sourceField: string) => {
    return mappings.find((m) => m.sourceField === sourceField)?.targetField;
  };

  return (
    <>
      <HeaderNew showSearch={false} />

      <div className="data-mapping-interface-hub">
        <div className="hub-content">
          <HubHeader
            title="Data Integration Hub"
            description="Centralized platform for seamless data integration and workflow management."
            showBackButton={true}
            backRoute={backRoute || MainRoutes.DATA_FILTER_BUILDER}
          />

          <div className="section-header">
            <div className="section-info">
              <h2>Data Mapping & Augmentation</h2>
              <p>
                Map IoT sensor data to IOGP document requirements for predictive
                maintenance analysis
              </p>
            </div>
          </div>

          <div className="mapping-interface-content">
            <div className="mapping-panels">
              {/* IoT Data Source Panel */}
              <div className="source-panel">
                <div className="panel-card">
                  <div className="panel-header">
                    <div className="header-icon">
                      <StorageIcon className="icon" />
                    </div>
                    <div className="header-info">
                      <h3>IoT Sensor Data</h3>
                      <p>{iotSchema.tableName}</p>
                    </div>
                  </div>

                  <div className="mapping-fields">
                    {selectedColumns.map((column) => (
                      <div key={column} className="field-mapping">
                        <div className="field-info">
                          <span className="field-name">{column}</span>
                          <span className="field-type">
                            {
                              iotSchema.columns.find((c) => c.name === column)
                                ?.type
                            }
                          </span>
                        </div>
                        <div className="mapping-select">
                          <select
                            value={getMappedTarget(column) || ""}
                            onChange={(e) =>
                              handleAddMapping(column, e.target.value)
                            }
                            className="target-select"
                          >
                            <option value="">Map to document field...</option>
                            {documentFields.map((field) => (
                              <option key={field.name} value={field.name}>
                                {field.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Document Data Target Panel */}
              <div className="target-panel">
                <div className="panel-card">
                  <div className="panel-header">
                    <div className="header-icon">
                      <ArticleIcon className="icon" />
                    </div>
                    <div className="header-info">
                      <h3>IOGP Requirements</h3>
                      <p>Extracted Document Data</p>
                    </div>
                  </div>

                  <div className="target-fields">
                    {documentFields.map((field) => {
                      const sourceMapping = mappings.find(
                        (m) => m.targetField === field.name
                      );
                      return (
                        <div
                          key={field.name}
                          className={`target-field ${
                            sourceMapping ? "mapped" : ""
                          }`}
                        >
                          <div className="field-content">
                            <div className="field-details">
                              <div className="field-label">{field.label}</div>
                              <div className="field-name">{field.name}</div>
                            </div>
                            {sourceMapping && (
                              <span className="mapped-badge">Mapped</span>
                            )}
                          </div>
                          {sourceMapping && (
                            <div className="mapping-indicator">
                              <ArrowForwardIcon className="arrow-icon" />
                              <span>From: {sourceMapping.sourceField}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Mapping Summary */}
            <div className="summary-section">
              <div className="summary-card">
                <div className="summary-header">
                  <div className="summary-icon">
                    <FlashOnIcon className="icon" />
                  </div>
                  <div className="summary-info">
                    <h3>Augmentation Configuration</h3>
                    <p>
                      {mappings.length} field{mappings.length !== 1 ? "s" : ""}{" "}
                      mapped
                    </p>
                  </div>
                  <span className="analysis-type-badge">
                    Predictive Maintenance Analysis
                  </span>
                </div>

                <div className="summary-stats">
                  <div className="stat-item">
                    <div className="stat-label">Active Filters</div>
                    <div className="stat-value">{filters.length}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-label">Data Sources</div>
                    <div className="stat-value">2</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="action-buttons">
              <div></div>
              <Button
                onClick={handleComplete}
                disabled={mappings.length === 0}
                variant="primary"
              >
                Complete Setup & Preview Results
                <ArrowForwardIcon />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DataMappingInterface;
